import { format, subMonths } from 'date-fns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useClient } from 'urql';

import { FeatureFlag } from '@revelio/core';
import {
  COMPOSITION_GET_DATA_V3,
  type CompositionV3Filters,
  Dimension1,
  ENTITY_V2_QUERY,
  EntityV2Type,
  EntKind,
  PAY_V2_QUERY,
  POSTING_GET_DATA,
  POSTING_SOURCE_IDS,
  POSTINGS_V3_GET_DATA,
  PositionsEntityKind,
  type PostingsDataQuery,
  type PostingsV3DataQuery,
  SENTIMENT_GET_MAP_DATA,
} from '@revelio/data-access';

import { useUnleashFlag } from '../../../shared/unleash-flags/use-unleash-flag';
import { buildCompositionV3Variables } from '../../CustomDash/filters/utils/composition-v3/build-composition-v3-variables';
import { appendRoleSelectionToFilters } from '../../CustomDash/filters/utils/role-skill-ent-kind';
import {
  buildIndividualPotentialPostingsV3Variables,
  type PostingsV3GeoSelection,
  parsePostingsV3Entry,
} from '../hooks/postingsV3Adapter';
import {
  type CompanyMetrics,
  parseEntity,
} from '../hooks/useCompetitorComposition';
import {
  computeGrowthPotential,
  computeStability,
  type EmployerHealth,
  generateSummary,
} from '../hooks/useEmployerHealth';
import {
  type CompanyPostings,
  parsePostingEntry,
} from '../hooks/usePostingsComparison';
import {
  buildCustomRoleFilter,
  type RoleLevel,
} from '../hooks/useResolveProfileIds';
import {
  BUCKET_SIZE,
  buildDistributionFromHistogram,
  findPercentileValue,
} from '../hooks/useSalaryDistribution';
import {
  type CompanySentiment,
  parseSentimentEntry,
} from '../hooks/useSentimentComparison';

interface EntityInfo {
  companyId: number;
  description: string | null;
  headquarters: string | null;
  founded: string | null;
}

export interface PercentileSalary {
  companyId: number;
  salaryAtPercentile: number | null;
}

export interface CompanyCompareResult {
  metrics: CompanyMetrics[];
  sentiments: CompanySentiment[];
  postings: CompanyPostings[];
  healthData: EmployerHealth[];
  entityInfo: EntityInfo[];
  percentileSalaries: PercentileSalary[];
  isLoading: boolean;
}

const getDateRange = () => {
  const now = new Date();
  return {
    start_date: format(subMonths(now, 6), 'yyyy-MM-dd'),
    end_date: format(now, 'yyyy-MM-dd'),
  };
};

/** Map geo type string to the posting filter key */
const GEO_FILTER_KEY: Record<string, string> = {
  country: 'country',
  msa: 'msa',
  city: 'city',
  region: 'region',
};

export const useCompanyCompareData = (
  rcids: number[],
  userRoleLevel: RoleLevel | null,
  userGeoId: number | null,
  userGeoType: string | null,
  isActive: boolean,
  userPercentile: number | null = null
): CompanyCompareResult => {
  const gqlClient = useClient();
  const isPostingsV3Enabled = useUnleashFlag(FeatureFlag.PostingsV3);
  const [metrics, setMetrics] = useState<CompanyMetrics[]>([]);
  const [sentiments, setSentiments] = useState<CompanySentiment[]>([]);
  const [postings, setPostings] = useState<CompanyPostings[]>([]);
  const [entityInfo, setEntityInfo] = useState<EntityInfo[]>([]);
  const [percentileSalaries, setPercentileSalaries] = useState<
    PercentileSalary[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const idsKey = rcids.join(',');
  const dateRange = useMemo(() => getDateRange(), []);

  const fetchData = useCallback(async () => {
    if (!isActive || rcids.length === 0) return;

    setIsLoading(true);

    try {
      const entities = rcids.map((id) => ({
        EntID: id,
        EntType: EntKind.Company,
      }));

      // Build posting filters with role + geo
      const postingFilters: Record<string, unknown> = {
        company: rcids,
        provider: [POSTING_SOURCE_IDS.unified],
        metric_mode: 'posting',
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
      };
      if (userRoleLevel) {
        postingFilters.custom_role = buildCustomRoleFilter(userRoleLevel);
      }
      if (userGeoId && userGeoType) {
        const geoKey = GEO_FILTER_KEY[userGeoType];
        if (geoKey) {
          postingFilters[geoKey] = [userGeoId];
        }
      }

      // Company entities preserve `rcids` order so the response (keyed by
      // `entity.index`) maps back to `rcids[index]`.
      const geoSelection: PostingsV3GeoSelection | null =
        userGeoId && userGeoType && userGeoType in GEO_FILTER_KEY
          ? {
              id: userGeoId,
              levelId: userGeoType as PostingsV3GeoSelection['levelId'],
            }
          : null;
      const postingsV3Variables = buildIndividualPotentialPostingsV3Variables({
        companyIds: rcids,
        customRole: userRoleLevel
          ? buildCustomRoleFilter(userRoleLevel)
          : undefined,
        geo: geoSelection,
        startDate: dateRange.start_date,
        endDate: dateRange.end_date,
      });

      // Build composition filters with role for more relevant metrics
      let compositionFilters: CompositionV3Filters = {};
      if (userRoleLevel) {
        for (const ent of buildCustomRoleFilter(userRoleLevel).entities ?? []) {
          if (ent?.id == null) continue;
          compositionFilters = appendRoleSelectionToFilters(
            compositionFilters,
            String(ent.levelId),
            Number(ent.id)
          );
        }
      }
      const compositionV3Variables = buildCompositionV3Variables({
        entities,
        filters: compositionFilters,
      });

      // Build salary distribution filters per company (for percentile comparison)
      const salaryFilters = {
        raw: false as const,
        ...(userRoleLevel
          ? { custom_role: buildCustomRoleFilter(userRoleLevel) }
          : {}),
      };

      // Fetch all data in parallel (separate typed batches)
      const [
        [compositionResult, sentimentResult, postingsResult],
        entityResults,
        payResults,
      ] = await Promise.all([
        Promise.all([
          gqlClient
            .query(COMPOSITION_GET_DATA_V3, compositionV3Variables)
            .toPromise(),
          gqlClient
            .query(SENTIMENT_GET_MAP_DATA, {
              filters: { company: rcids },
              dim1: Dimension1.Company,
            })
            .toPromise(),
          isPostingsV3Enabled
            ? gqlClient
                .query(POSTINGS_V3_GET_DATA, postingsV3Variables)
                .toPromise()
            : gqlClient
                .query(POSTING_GET_DATA, {
                  filters: postingFilters,
                  dim1: Dimension1.Company,
                })
                .toPromise(),
        ]),
        // Entity V2 queries (one per company)
        Promise.all(
          rcids.map((rcid) =>
            gqlClient
              .query(ENTITY_V2_QUERY, {
                req: { entID: rcid, entType: EntityV2Type.Company },
              })
              .toPromise()
          )
        ),
        // PAY_V2 salary distribution per company (for percentile lookup)
        Promise.all(
          rcids.map((rcid) =>
            gqlClient
              .query(PAY_V2_QUERY, {
                req: {
                  entities: [
                    { EntID: rcid, EntType: PositionsEntityKind.Company },
                  ],
                  filters: salaryFilters,
                },
              })
              .toPromise()
          )
        ),
      ]);

      if (!mountedRef.current) return;

      // Parse composition
      const parsedMetrics: CompanyMetrics[] = [];
      const compositionEntities =
        compositionResult.data?.composition_v3?.entities;
      if (compositionEntities) {
        for (const entity of compositionEntities) {
          parsedMetrics.push(parseEntity(entity));
        }
      }
      setMetrics(parsedMetrics);

      // Parse sentiment
      const parsedSentiments: CompanySentiment[] = [];
      const rawSentiment = sentimentResult.data?.sentiment;
      if (rawSentiment && Array.isArray(rawSentiment)) {
        for (const entry of rawSentiment) {
          if (entry) parsedSentiments.push(parseSentimentEntry(entry));
        }
      }
      setSentiments(parsedSentiments);

      // Parse postings
      const parsedPostings: CompanyPostings[] = [];
      if (isPostingsV3Enabled) {
        // Response entities are keyed by request index, so each maps back to
        // `rcids[entity.index]` (the response does not echo the company id). The
        // posting slot is a v2/v3 union; the flag fixes which query ran.
        const v3Data = postingsResult.data as PostingsV3DataQuery | undefined;
        const v3Entities = v3Data?.postings_v3?.entities;
        if (v3Entities) {
          for (const entity of v3Entities) {
            const companyId = rcids[entity.index];
            if (companyId === undefined) continue;
            parsedPostings.push(parsePostingsV3Entry(entity, companyId));
          }
        }
      } else {
        const v2Data = postingsResult.data as PostingsDataQuery | undefined;
        const rawPostings = v2Data?.posting;
        if (rawPostings && Array.isArray(rawPostings)) {
          for (const entry of rawPostings) {
            if (entry) parsedPostings.push(parsePostingEntry(entry));
          }
        }
      }
      setPostings(parsedPostings);

      // Parse entity info
      const parsedEntityInfo: EntityInfo[] = [];
      for (let i = 0; i < rcids.length; i++) {
        const rcid = rcids[i];
        if (rcid === undefined) continue;
        const result = entityResults[i];
        const entity = result?.data?.entityV2?.entity;
        parsedEntityInfo.push({
          companyId: rcid,
          description: entity?.description ?? null,
          headquarters: entity?.headquarters ?? null,
          founded: entity?.founded ?? null,
        });
      }
      setEntityInfo(parsedEntityInfo);

      // Parse percentile salaries from pay distributions
      const parsedPercentiles: PercentileSalary[] = [];
      const targetCdf = userPercentile !== null ? userPercentile / 100 : 0.5;
      for (let i = 0; i < rcids.length; i++) {
        const rcid = rcids[i];
        if (rcid === undefined) continue;
        const payResult = payResults[i];
        const histogram =
          payResult?.data?.pay_v2?.entities?.[0]?.metrics?.histogram?.base;

        let salaryAtPercentile: number | null = null;
        if (histogram && histogram.length > 0) {
          const dist = buildDistributionFromHistogram(histogram, BUCKET_SIZE);
          if (dist.length > 0) {
            salaryAtPercentile = findPercentileValue(dist, targetCdf);
          }
        }
        parsedPercentiles.push({ companyId: rcid, salaryAtPercentile });
      }
      setPercentileSalaries(parsedPercentiles);
    } catch {
      // Silently handle errors — partial data is fine
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    rcids,
    isActive,
    gqlClient,
    userRoleLevel,
    userGeoId,
    userGeoType,
    userPercentile,
    dateRange,
    isPostingsV3Enabled,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    fetchData();
  }, [idsKey, isActive, isPostingsV3Enabled]);

  // Derive health data
  const healthData = useMemo((): EmployerHealth[] => {
    if (metrics.length === 0) return [];

    const sentimentMap = new Map(sentiments.map((s) => [s.companyId, s]));
    const postingsMap = new Map(postings.map((p) => [p.companyId, p]));

    return metrics.map((m) => {
      const sentiment = sentimentMap.get(m.companyId);
      const posting = postingsMap.get(m.companyId);
      const stability = computeStability(m, sentiment, posting);
      const hiringVelocity = m.hiringTrend;
      const growthPotential = computeGrowthPotential(m);

      return {
        companyId: m.companyId,
        shortName: m.shortName,
        stabilityScore: stability.score,
        risk: stability.risk,
        hiringVelocity,
        growthPotential,
        summaryText: generateSummary(
          m.shortName,
          stability,
          hiringVelocity,
          growthPotential
        ),
      };
    });
  }, [metrics, sentiments, postings]);

  return {
    metrics,
    sentiments,
    postings,
    healthData,
    entityInfo,
    percentileSalaries,
    isLoading,
  };
};
